/* eslint-disable max-statements */
import { useContext, useState } from 'react';

import type { AdjoiningSeamMarker } from '../helpers/get-adjoining-countertop-seams';
import { AppContext } from '@r-app/app-context';

export type RenderAdjoiningSeamMarkerProps = {
	marker: AdjoiningSeamMarker;
	fontSize: number;
};

const SEAM_COLOR = '#C00000';

/**
 * A single adjoining-countertop SEAM marker: a red dashed line along the meeting
 * vector. With a label, the `x" SEAM` dimension sets inline in the middle (dashes
 * on both sides); without one (side-edge seams) it is a bare dashed line drawn
 * exactly on the slab edge. Deletable via a hover-✕ affordance, persisted in the
 * kitchen's blueprintOverrides the same way CLEAN EDGE labels are.
 */
export const RenderAdjoiningSeamMarker = (props: RenderAdjoiningSeamMarkerProps): React.ReactElement => {
	const { marker, fontSize } = props;
	const { controller } = useContext(AppContext);
	const { kitchen, options } = controller;
	const { x1, y1, x2, y2, label } = marker;
	const [hovered, setHovered] = useState(false);
	const [deleted, setDeleted] = useState(false);

	const deletedKey = `seam.deleted.${marker.id}`;
	const deletable = !options.readonly;
	const isDeleted = deleted || Boolean(kitchen?.blueprintOverrides?.[deletedKey]);

	if (isDeleted) return <g />;

	const midX = (x1 + x2) / 2;
	const midY = (y1 + y2) / 2;
	const isHorizontal = Math.abs(y2 - y1) < Math.abs(x2 - x1);
	const rotation = isHorizontal ? undefined : `rotate(-90, ${midX}, ${midY})`;

	const dashProps = {
		stroke: SEAM_COLOR,
		strokeWidth: 0.4,
		strokeDasharray: '2 1.5'
	};

	const deleteHandle = deletable && hovered && (
		<text
			x={midX + fontSize}
			y={midY - fontSize}
			fill={SEAM_COLOR}
			fontSize={fontSize}
			fontWeight="bold"
			textAnchor="middle"
			style={{ userSelect: 'none', cursor: 'pointer' }}
			transform={rotation}
			onPointerDown={(event) => {
				event.stopPropagation();
				setDeleted(true);
				kitchen?.setBlueprintOverride(deletedKey, '1');
			}}
		>
			✕
		</text>
	);

	// Bridges the gap between the line/label and the offset delete-✕ so the pointer never leaves the
	// <g>'s painted hit area while crossing from one to the other (same transform anchor as the ✕).
	const bridgeTargetX = midX + fontSize;
	const bridgeTargetY = midY - fontSize;
	const bridgePad = fontSize / 2;
	const deleteBridge = deletable && (
		<rect
			x={Math.min(midX, bridgeTargetX) - bridgePad}
			y={Math.min(midY, bridgeTargetY) - bridgePad}
			width={Math.abs(bridgeTargetX - midX) + (bridgePad * 2)}
			height={Math.abs(bridgeTargetY - midY) + (bridgePad * 2)}
			fill="transparent"
			style={{ pointerEvents: 'auto' }}
			transform={rotation}
		/>
	);

	if (!label) {
		return (
			<g onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
				<line x1={x1} y1={y1} x2={x2} y2={y2} {...dashProps} />
				{deleteBridge}
				{deleteHandle}
			</g>
		);
	}

	const labelHalf = (label.length * fontSize * 0.34) + fontSize;

	return (
		<g onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
			{isHorizontal ? (
				<>
					<line x1={x1} y1={y1} x2={midX - labelHalf} y2={y1} {...dashProps} />
					<line x1={midX + labelHalf} y1={y2} x2={x2} y2={y2} {...dashProps} />
				</>
			) : (
				<>
					<line x1={x1} y1={y1} x2={x1} y2={midY - labelHalf} {...dashProps} />
					<line x1={x2} y1={midY + labelHalf} x2={x2} y2={y2} {...dashProps} />
				</>
			)}
			<text
				x={midX}
				y={midY + (fontSize * 0.35)}
				fill={SEAM_COLOR}
				fontSize={fontSize}
				fontWeight="bold"
				textAnchor="middle"
				style={{ userSelect: 'none' }}
				transform={rotation}
			>
				{label}
			</text>
			{deleteBridge}
			{deleteHandle}
		</g>
	);
};
